import React, { useEffect, useState, ChangeEvent } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import iconv from 'iconv-lite';
import { parse } from 'csv-parse/lib/sync';
import {
  NameDevSetModel,
  PutNameDevSetCsvData,
  CheckInputData,
  ReadAsCsvReader,
  CreateNameDevSetList,
} from './ReadDevCsvDialog';
import ReadDevDialogGridRow from './ReadDevCsvDialogGridRow';
import ConfirmDialog, { ConfirmDialogResponse } from '../../../atoms/Dialog/ConfirmDialog';
import MessageDialog, { MessageDialogResponse } from '../../../atoms/Dialog/MessageDialog';
import { useModal } from '../../../../function/useModal';
import { useTranslation } from 'react-i18next';
import { ResponceDataType, ErrorCode } from '../../../../ErrorCode';

type ReadDevCsvDialogPageProps = {
  open: boolean;
  onClose: (updateFlg: boolean) => void;
  tableContainerStyle: string;
  headerCellStyle: string;
  bodyCellStyle: string;
  textFieldStyle: string;
  fileObject: File | undefined;
};

const ReadDevCsvDialogPage: React.FC<ReadDevCsvDialogPageProps> = (props) => {
  const { t } = useTranslation();
  const [filename, setFilename] = useState('');
  const [readDevList, setReadDevList] = useState<NameDevSetModel[]>([]);
  const [buttonEnable, setButtonEnable] = useState<boolean>(false);

  const [flgCfmDlg, actCfmDlg] = useModal<ConfirmDialogResponse>();
  const [flgMsgDlg, actMsgDlg] = useModal<MessageDialogResponse>();
  const [dlgmsg, setDlgmsg] = useState<string>('');

  const cfmdlg = flgCfmDlg ? (
    <ConfirmDialog
      dialog={actCfmDlg}
      message={dlgmsg}
      action={t('ReadDevCsvDialogPage.はい', 'はい')}
      cancel={t('ReadDevCsvDialogPage.いいえ', 'いいえ')}
    />
  ) : null;

  const msgdlg = flgMsgDlg ? <MessageDialog dialog={actMsgDlg} message={dlgmsg} /> : null;

  useEffect(() => {
    if (props.open === true && props.fileObject != null) {
      console.debug(['ダイアログOpen event'], props);
      const file: File = props.fileObject;
      console.debug(['fileObjectData'], file);

      ReadAsCsvReader(file)
        .then((r) => {
          if (r instanceof ArrayBuffer) {
            return CreateNameDevSetList(r);
          } else {
            throw new Error('');
          }
        })
        .then((r) => {
          setFilename(file.name);
          setReadDevList([...r]);
        })
        .catch((error) => {
          setFilename('');
          setReadDevList([]);
          setDlgmsg(
            t(
              'ReadDevCsvDialogPage.CSVファイル読込に失敗しました。CSVファイルの内容を確認してください',
              'CSVファイル読込に失敗しました。CSVファイルの内容を確認してください'
            )
          );
          actMsgDlg.open();
          console.error(error);
        })
        .finally(() => {
          setButtonEnable(true);
        });
    }
  }, [props.open]);

  /**
   * ファイル選択ダイアログ処理
   * @param e
   * @returns
   */
  const onFileSelectAction = (e: ChangeEvent<HTMLInputElement>) => {
    //console.debug(['ReadDevCsvDialogPage - onFileSelectAction']);
    setButtonEnable(false);
    if (!e.target.files) {
      setButtonEnable(true);
      return;
    }
    const file: File = e.target.files[0];
    fileSelectAction(file);

    //選択はクリアする
    e.target.value = '';
  };

  /**
   *ファイルobjectより、画面でセットする値を読込
   * @param file
   */
  const fileSelectAction = (file: File) => {
    console.debug(['fileObjectData'], file);
    ReadAsCsvReader(file)
      .then((r) => {
        if (r instanceof ArrayBuffer) {
          return CreateNameDevSetList(r);
        } else {
          throw new Error('');
        }
      })
      .then((r) => {
        setFilename(file.name);
        setReadDevList([...r]);
      })
      .catch((error) => {
        setFilename('');
        setReadDevList([]);
        setDlgmsg(
          t(
            'ReadDevCsvDialogPage.CSVファイル読込に失敗しました。CSVファイルの内容を確認してください',
            'CSVファイル読込に失敗しました。CSVファイルの内容を確認してください'
          )
        );
        actMsgDlg.open();
        console.error(error);
      })
      .finally(() => {
        setButtonEnable(true);
      });
  };

  /**
   *保存処理
   * @returns
   */
  const saveAction = (target: NameDevSetModel[]) => {
    console.debug(['saveAction - start', target]);
    //入力チェック
    console.debug(['saveAction - CheckInputData']);
    const messages = CheckInputData(target, t);
    if (messages.length !== 0) {
      console.debug(['saveAction - CheckInputData error', messages]);
      setDlgmsg(messages.join('\n'));
      actMsgDlg.open();
      return;
    }
    setButtonEnable(false);
    PutNameDevSetCsvData(target)
      .then((r) => {
        console.debug(t('ReadDevCsvDialogPage.機器登録処理実施結果　OK', '機器登録処理実施結果　OK'));
        setDlgmsg(t('ReadDevCsvDialogPage.保存が正常に終了しました', '保存が正常に終了しました'));
        actMsgDlg.open().apply(() => {
          setFilename('');
          setReadDevList([]);
          props.onClose(true);
        });
      })
      .catch((error) => {
        console.debug(t('ReadDevCsvDialogPage.機器登録処理実施結果　NG', '機器登録処理実施結果　NG'));
        setDlgmsg(t('ReadDevCsvDialogPage.保存中にエラーが発生しました', '保存中にエラーが発生しました'));
        actMsgDlg.open();
        console.error(['saveAction error', error]);
      })
      .finally(() => {
        setButtonEnable(true);
      });
    console.debug(['saveAction - end', readDevList, target]);
  };

  /**
   *データ変更通知
   * @returns
   */
  const changeAction = (target: NameDevSetModel) => {
    console.debug(['changeAction - start', readDevList, target]);
    var index = readDevList.findIndex((r) => r.indexNo === target.indexNo);
    readDevList[index] = target;
    console.debug(['changeAction - end', readDevList, target]);
  };

  return (
    <>
      <Dialog open={props.open}>
        <DialogTitle id="alert-dialog-title">
          {t('ReadDevCsvDialogPage.機器管理台帳読込', '機器管理台帳読込')}
          {`[${filename}]`}
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} className={props.tableContainerStyle}>
            <Table stickyHeader aria-label="a dense table">
              <TableHead>
                <TableRow>
                  <TableCell className={props.headerCellStyle}>{'No'}</TableCell>
                  <TableCell className={props.headerCellStyle}>
                    {t('ReadDevCsvDialogPage.機器番号', '機器番号')}
                  </TableCell>
                  <TableCell className={props.headerCellStyle}>
                    {' '}
                    {t('ReadDevCsvDialogPage.機器名称', '機器名称')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {readDevList.map((row, index) => {
                  return (
                    <ReadDevDialogGridRow
                      key={index}
                      nameDevRow={row}
                      onChangeData={changeAction}
                      bodyCellStyle={props.bodyCellStyle}
                      textFieldStyle={props.textFieldStyle}
                    ></ReadDevDialogGridRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button
            id="fileReadButton"
            component="label"
            variant="contained"
            color="primary"
            disabled={!buttonEnable}
            disableElevation
          >
            {t('ReadDevCsvDialogPage.機器台帳読込', '機器台帳読込')}
            <input type="file" accept=".csv" hidden onChange={onFileSelectAction} />
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!buttonEnable}
            disableElevation
            onClick={() => saveAction(readDevList)}
          >
            {t('ReadDevCsvDialogPage.保存', '保存')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            onClick={() => {
              setFilename('');
              setReadDevList([]);
              props.onClose(false);
            }}
            disabled={!buttonEnable}
          >
            {t('ReadDevCsvDialogPage.閉じる', '閉じる')}
          </Button>
        </DialogActions>
      </Dialog>
      {msgdlg}
    </>
  );
};

export default ReadDevCsvDialogPage;
